import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { CollaboratorInvite, CollaboratorInviteInput, CollaboratorMember, ID, PlannerRole } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export const roleLabels: Record<PlannerRole, string> = {
  owner: "Owner",
  partner: "Partner",
  coordinator: "Coordinator",
  view_only: "View only"
};

const inviteStatusTone: Record<CollaboratorInvite["status"], "neutral" | "warning" | "success" | "error"> = {
  pending: "warning",
  accepted: "success",
  revoked: "neutral",
  expired: "error"
};

export function CollaboratorsScreen() {
  const { workspace, session, loading, error, service, run } = usePlannerData();
  const [inviting, setInviting] = React.useState(false);
  const [invitingAttempt, setInvitingAttempt] = React.useState(0);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: sendingInvite, guardedSubmit: guardedInvite } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey(invitingAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading collaborators" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const canManage = session?.user.role === "owner" || session?.user.role === "partner";
  const members = workspace.collaboratorMembers;
  const pendingInvites = workspace.collaboratorInvites.filter((invite) => invite.status === "pending");
  const pastInvites = workspace.collaboratorInvites.filter((invite) => invite.status !== "pending");

  const sendInvite = (input: CollaboratorInviteInput) => guardedInvite(async () => {
    setActionError(null);
    try {
      await run(() => service.collaborators.invite(input, idempotencyKey));
      setInviting(false);
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });

  const resend = async (inviteId: ID) => {
    setActionError(null);
    try {
      await run(() => service.collaborators.resendInvite(inviteId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const revoke = async (inviteId: ID) => {
    setActionError(null);
    try {
      await run(() => service.collaborators.revokeInvite(inviteId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const changeRole = async (membershipId: ID, role: PlannerRole) => {
    setActionError(null);
    try {
      await run(() => service.collaborators.changeRole(membershipId, role));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const remove = async (membershipId: ID) => {
    setActionError(null);
    try {
      await run(() => service.collaborators.removeMember(membershipId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  if (!canManage) {
    return (
      <div>
        <PageHeader eyebrow="Account" title="Collaborators" meta={[{ icon: "users", label: `${members.length} on this wedding` }]} />
        <div className="pob-pad">
          <Callout tone="neutral" title="Owner and Partner only">
            Only the Owner or Partner can invite, remove, or change the role of a collaborator. Ask them if you need someone added.
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Account"
        title="Collaborators"
        meta={[{ icon: "users", label: `${members.length} active` }, { icon: "mail", label: `${pendingInvites.length} pending invite${pendingInvites.length === 1 ? "" : "s"}` }]}
        actions={<Button variant="primary" icon="user-plus" onClick={() => { setInvitingAttempt((n) => n + 1); setInviting(true); }}>Invite collaborator</Button>}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Collaborator action failed">{actionError}</Callout>}
        <Card eyebrow="Active" title="On this wedding" padding="0">
          <DataTable
            rows={members}
            emptyLabel="No collaborators yet"
            columns={[
              { key: "name", header: "Name", render: (member) => <span style={{ font: "var(--type-label)" }}>{member.name}</span> },
              { key: "email", header: "Email", muted: true },
              { key: "personName", header: "Linked guest", muted: true, render: (member) => member.personName || "-" },
              { key: "role", header: "Role", width: 160, render: (member) => <Select size="sm" value={member.role} onChange={(event) => changeRole(member.id, event.target.value as PlannerRole)} options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))} /> },
              { key: "actions", header: "", width: 90, render: (member) => <Button size="sm" variant="danger" onClick={() => remove(member.id)}>Remove</Button> }
            ]}
          />
        </Card>
        <Card eyebrow="Pending" title="Invited, not yet joined" padding="0">
          <DataTable
            rows={pendingInvites}
            emptyLabel="No pending invites"
            columns={[
              { key: "email", header: "Email", render: (invite) => <span style={{ font: "var(--type-label)" }}>{invite.email}</span> },
              { key: "role", header: "Role", muted: true, render: (invite) => roleLabels[invite.role] },
              { key: "expiresAt", header: "Expires", muted: true },
              { key: "status", header: "Status", render: (invite) => <Badge dot tone={inviteStatusTone[invite.status]}>{invite.status}</Badge> },
              { key: "actions", header: "", width: 160, render: (invite) => <span style={{ display: "flex", gap: "var(--space-2)" }}><Button size="sm" onClick={() => resend(invite.id)}>Resend</Button><Button size="sm" variant="danger" onClick={() => revoke(invite.id)}>Revoke</Button></span> }
            ]}
          />
        </Card>
        {pastInvites.length > 0 && (
          <Card eyebrow="History" title="Past invites" padding="0">
            <DataTable
              dense
              rows={pastInvites}
              columns={[
                { key: "email", header: "Email" },
                { key: "role", header: "Role", muted: true, render: (invite) => roleLabels[invite.role] },
                { key: "status", header: "Status", render: (invite) => <Badge dot tone={inviteStatusTone[invite.status]}>{invite.status}</Badge> }
              ]}
            />
          </Card>
        )}
      </div>
      {inviting && <InviteDialog submitting={sendingInvite} onClose={() => setInviting(false)} onSubmit={sendInvite} />}
    </div>
  );
}

function InviteDialog({ onClose, onSubmit, submitting }: { onClose: () => void; onSubmit: (input: CollaboratorInviteInput) => void; submitting?: boolean }) {
  const { workspace } = usePlannerData();
  const [draft, setDraft] = React.useState<CollaboratorInviteInput>({ email: "", role: "coordinator" });
  if (!workspace) return null;
  const valid = /.+@.+\..+/.test(draft.email.trim());
  const personOptions = [{ value: "", label: "No linked guest" }, ...workspace.people.map((person) => ({ value: person.id, label: person.name }))];
  return (
    <Dialog
      title="Invite collaborator"
      description="They'll get an email with a link to accept. Family/Friend relationship labels are separate from this role."
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit({ ...draft, personId: draft.personId || undefined })}>{submitting ? "Sending…" : "Send invite"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Email"><Input value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} placeholder="name@example.com" /></Field>
        <Field label="Role"><Select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as PlannerRole })} options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Linked guest (optional)"><Select value={draft.personId || ""} onChange={(event) => setDraft({ ...draft, personId: event.target.value || undefined })} options={personOptions} /></Field>
      </div>
    </Dialog>
  );
}
